import i18n from "i18n";
import { getDasUrl } from "actions/config";

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export function getPixelPosition(x, y, cellWidth, cellHeight) {
    return {
        left: x * cellWidth,
        top: y * cellHeight,
    };
}

export const predefinedPoints = {
    Restaurantes: [
        { id: 1, name: "Pizza Hut", icon: "🍕", quantity: 3 },
        { id: 2, name: "Domino's", icon: "🍕", quantity: 4 },
        { id: 3, name: "Little Caesars", icon: "🍕", quantity: 3 },
        { id: 4, name: "McDonald's", icon: "🍔", quantity: 5 },
        { id: 5, name: "Burger King", icon: "🍔", quantity: 3 },
    ],
    Hoteles: [
        { id: 6, name: "Hotel Plaza", icon: "🏨", quantity: 2 },
        { id: 7, name: "Hilton Resort", icon: "🏩", quantity: 3 },
    ],
    Parques: [
        { id: 8, name: "Central Park", icon: "🌳", quantity: 3 },
        { id: 9, name: "Green Valley", icon: "🌴", quantity: 2 },
    ],
    Tiendas: [
        { id: 10, name: "Supermercado Express", icon: "🛒", quantity: 3 },
        { id: 11, name: "Mega Mall", icon: "🏬", quantity: 2 },
    ],
    Estaciones: [
        { id: 12, name: "Estación Central", icon: "🚉", quantity: 3 },
        { id: 13, name: "Terminal Express", icon: "🚏", quantity: 4 },
    ],
};

export const generateRandomCategories = () => {
    const totalColumns = 50,
        totalRows = 29;
    const xMax = totalColumns - 1;
    const yMax = totalRows - 1;

    return Object.entries(predefinedPoints).map(([categoryName, items]) => ({
        name: categoryName,
        points: items.flatMap(({ id, name, icon, quantity }) =>
            Array.from({ length: quantity }, () => ({
                id,
                name,
                icon,
                coord: [randomInt(1, xMax), randomInt(1, yMax)],
            }))
        ),
    }));
};

export const arrangeGridPointOfInterest = (pointsOfInterest = []) => {
    const lang = (i18n.language || "en").split("-")[0];
    const dasURL = getDasUrl();

    return pointsOfInterest.flatMap((point) => {
        const { pinIcon = {}, pins = [], id } = point;

        const icon = pinIcon.icon && pinIcon.lib ? { icon: pinIcon.icon, lib: pinIcon.lib } : null;

        const srcImage = pinIcon.libraryRef
            ? `${dasURL}/${pinIcon.libraryRef}?lang=${lang}`
            : pinIcon.externalUrl || null;

        return pins.map((pin) => ({
            x: pin.x,
            y: pin.y,
            icon,
            srcImage,
            id,
            zIndex: 20 + pin.y,
        }));
    });
};

export const getImageUrlAndAdjust = (mapData, dasURL) => {
    if (!mapData) return { imageUrl: null, adjust: null };

    let imageUrl = null;

    if (mapData?.image?.libraryRef) {
        imageUrl = `${dasURL}/${mapData.image.libraryRef}`;
    } else if (mapData?.image?.externalUrl) {
        imageUrl = mapData.image.externalUrl;
    }

    let adjust = null;

    switch (mapData?.image?.adjust) {
        case "cover-adjust":
            adjust = "100% 100%";
            break;
        case "cover":
        case "contain":
            adjust = mapData?.image?.adjust;
            break;
        default:
            adjust = null;
    }

    return { imageUrl, adjust };
};

const getUserAgent = () => {
    if (typeof navigator === "undefined") {
        return "";
    }
    const fallbackOpera = typeof window !== "undefined" ? window.opera : undefined;
    return navigator.userAgent || navigator.vendor || fallbackOpera || "";
};

export const isIOS = () => {
    const ua = getUserAgent();
    const platform = typeof navigator !== "undefined" ? navigator.platform : "";
    const touchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0;
    return /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && touchPoints > 1);
};

export const isAndroid = () => /Android/i.test(getUserAgent());

export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
export const smoothstep = (edge0, edge1, x) => {
    const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
};

export const DEFAULTS_SCALE_PIN = {
    zoomMin: 0.1,
    zoomMax: 20,
    deadzone: 0.1,
    tEnd: 1.2,
    baseStrength: 0.95,
    quant: 0.012,
    min: 0.8,
    max: 2.5,
    rerenderStep: 0.08,
    activeScale: 2.1,
    defaultScale: 1.9,
};

export function usePinScale(zoomScale, { isActive = false, overrides = {} } = {}) {
    const C = { ...DEFAULTS_SCALE_PIN, ...overrides };

    const z = clamp(zoomScale || 1, C.zoomMin, C.zoomMax);
    const dist = Math.abs(Math.log2(z));
    const t = smoothstep(C.deadzone, C.tEnd, dist);
    const strength = C.baseStrength + (1 - C.baseStrength) * t;
    const scaleConst = isActive ? C.activeScale : C.defaultScale;

    let pinScale = scaleConst * z ** -strength;
    pinScale = Math.round(pinScale / C.quant) * C.quant;
    pinScale = clamp(pinScale, C.min, C.max);

    const iconRenderKey = Math.round(Math.log2(z) / C.rerenderStep);
    const transform = `translateZ(0) scale(${pinScale})`;

    return { pinScale, iconRenderKey, transform };
}
